import React from 'react';
import { Form, Modal, Button, Icon } from 'semantic';
import { useTranslation } from 'react-i18next';
import { request } from 'utils/api';
import { useMutation } from '@tanstack/react-query';
import MarketEnergyTariffs from 'components/MarketEnergyTariffs/Current';
import { EnergyMarketTariffProvider } from 'components/MarketEnergyTariffs/Context';
import { Layout } from 'components/Layout';
import { Formik } from 'formik';
import * as Yup from 'yup';

import PriceField from 'components/form-fields/formik/PriceField';
import { formatCurrency } from 'utils/formatting';
import { useAccount } from 'contexts/account';
import { useAccountPlatformFeatures } from 'components/account-platform-features/context';
import ErrorMessage from 'components/ErrorMessage';
import { EvseInvite } from 'types/evse-invite';

export default function EditInvitePrice({
  trigger,
  onSuccess = () => {},
  onClose = () => {},
  initialValues,
}: {
  trigger: React.ReactNode;
  onSuccess?: (data: any) => void;
  onClose?: () => void;
  initialValues: EvseInvite;
}) {
  const { account } = useAccount();

  const { accountHasPlatformFeature } = useAccountPlatformFeatures();

  const evseInvite = initialValues;

  const [open, setOpen] = React.useState(false);

  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async (values: any) => {
      return request({
        method: 'PATCH',
        path: `/1/evse-invites/${evseInvite.id}`,
        body: {
          pricePerKwh: Number(values.pricePerKwh),
          surchargePerKwh: Number(values.surchargePerKwh),
          enableEnergyMarketTracking: values.enableEnergyMarketTracking,
        },
      });
    },
    onSuccess: () => {
      setOpen(false);
      onSuccess({});
      onClose();
    },
  });

  const schema = Yup.object({
    pricePerKwh: Yup.number().required(
      t('modals.inviteEmployee.pricePerKwh', 'Please enter a price per kWh!')
    ),
    enableEnergyMarketTracking: Yup.boolean().required(),
    surchargePerKwh: Yup.number().default(0),
  });

  const costSettings = {
    pricePerKwh: evseInvite.pricePerKwh,
    surchargePerKwh: evseInvite.surchargePerKwh || 0,
    enableEnergyMarketTracking: evseInvite.enableEnergyMarketTracking || false,
  };

  const showDynamicTariffs =
    account?.billing?.countryCode?.toUpperCase() === 'NL' &&
    accountHasPlatformFeature(
      'advanced-tariffs:dynamic-employee-reimbursement'
    );

  return (
    <Modal
      size="small"
      closeIcon
      trigger={trigger}
      onClose={() => {
        setOpen(false);
        onClose();
      }}
      onOpen={() => setOpen(true)}
      open={open}>
      <Modal.Header>
        {t('editInvitePrice.title', 'Update Price Settings')}
      </Modal.Header>
      <Formik
        onSubmit={(values) => {
          return mutation.mutateAsync(values);
        }}
        initialValues={{
          ...costSettings,
          pricePerKwh: costSettings.pricePerKwh || 0,
          surchargePerKwh: costSettings.surchargePerKwh || 0,
        }}
        validationSchema={schema}>
        {({ values, isSubmitting, resetForm, handleSubmit }) => {
          return (
            <>
              <Modal.Content>
                <Form id="edit-location-price-form" onSubmit={handleSubmit}>
                  <p style={{ fontWeight: 'bold' }}>
                    {t(
                      'editLocationPrice.description',
                      'Charging price to reimburse to employee by'
                    )}
                  </p>
                  {/* only allow dynamic-employee-reimbursement in NL as market price is only avaible in nl */}
                  {showDynamicTariffs && (
                    <Layout horizontal style={{ marginBottom: '15px' }}>
                      <Button
                        basic
                        color={
                          !values.enableEnergyMarketTracking ? 'blue' : null
                        }
                        style={{
                          width: '100%',
                          height: '55px',
                          fontWeight: 'bold',
                        }}
                        onClick={(e: React.MouseEvent) => {
                          // stop the form from submitting
                          e.preventDefault();

                          resetForm({
                            values: {
                              ...values,
                              enableEnergyMarketTracking: false,
                              surchargePerKwh: 0,
                            },
                          });
                        }}>
                        <Layout horizontal>
                          <Icon
                            style={{ marginRight: '1em' }}
                            name={
                              !values.enableEnergyMarketTracking
                                ? 'circle-dot'
                                : 'circle regular'
                            }
                          />
                          {t('evseInviteModal.pricePerKWh', 'Price per kWh')}
                        </Layout>
                      </Button>
                      <Button
                        basic
                        color={
                          values.enableEnergyMarketTracking ? 'blue' : null
                        }
                        style={{
                          width: '100%',
                          height: '55px',
                          fontWeight: 'bold',
                        }}
                        onClick={(e: React.MouseEvent) => {
                          // stop the form from submitting
                          e.preventDefault();
                          resetForm({
                            values: {
                              ...values,
                              enableEnergyMarketTracking: true,
                            },
                          });
                        }}>
                        <Layout horizontal>
                          <Icon
                            style={{ marginRight: '1em' }}
                            name={
                              values.enableEnergyMarketTracking
                                ? 'circle-dot'
                                : 'circle regular'
                            }
                          />
                          {t('evseInviteModal.dynamicTariff', 'Dynamic tariff')}
                        </Layout>
                      </Button>
                    </Layout>
                  )}

                  {!values.enableEnergyMarketTracking && (
                    <PriceField
                      currency={account?.billingCurrency}
                      label={t('evseInviteModal.pricePerKwh')}
                      name="pricePerKwh"
                      required
                      wrapperStyle={{ marginBottom: '15px' }}
                    />
                  )}

                  {values.enableEnergyMarketTracking && (
                    <>
                      {/*
                        Area hardcoded to NL for now. This is a product decision and it means that
                        NL dynamic pricing will be applied to stations that use this tariff profile
                        even if they are not located in NL.

                        This might be changed in the future making the area selectable.
                        */}
                      <EnergyMarketTariffProvider energyDeliveryArea={'NL'}>
                        <MarketEnergyTariffs />
                      </EnergyMarketTariffProvider>
                      <Form.Field>
                        <label>
                          {t('evseInviteModal.surcharge', 'Surcharge')}
                        </label>
                        <div>
                          {t(
                            'evseInviteModal.surchargePerKwhDescription',
                            'Set a surcharge to cover for additional costs the employee may incur on top of the market price.'
                          )}
                        </div>
                        <PriceField
                          currency={account?.billingCurrency}
                          name="surchargePerKwh"
                          wrapperStyle={{ marginBottom: '15px' }}
                        />
                      </Form.Field>
                      <Form.Field required>
                        <label>
                          {t('evseInviteModal.backupTarriff', 'Backup tariff')}
                        </label>
                        <div>
                          {t(
                            'evseInviteModal.backupTarriffDescription',
                            'Set your backup price in case the current energy market price is not available. The surcharge price from the dynamic tariff will still be applied.'
                          )}
                        </div>
                        <PriceField
                          currency={account?.billingCurrency}
                          name="pricePerKwh"
                          required
                        />
                        {values.surchargePerKwh > 0 &&
                          values.pricePerKwh > 0 && (
                            <div style={{ marginTop: '4px' }}>
                              {t(
                                'evseInviteModal.combinedTariffDescription',
                                '{{price}} per kWh including surcharge to reimburse.',
                                {
                                  price: formatCurrency(
                                    Number(values.surchargePerKwh) +
                                      Number(values.pricePerKwh),
                                    account?.billingCurrency
                                  ),
                                }
                              )}
                            </div>
                          )}
                      </Form.Field>
                    </>
                  )}
                </Form>
                <ErrorMessage error={mutation.error} />
              </Modal.Content>
              <Modal.Actions>
                <Button
                  basic
                  onClick={() => {
                    setOpen(false);
                  }}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  primary
                  form="edit-location-price-form"
                  floated="right"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  content={t('common.update', 'Update')}
                />
              </Modal.Actions>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
}
